import React from 'react';
import {Avatar, Tooltip, Typography} from '@mui/material';
import {InfoOutlined, PhoneAndroid} from '@mui/icons-material';
import BaseModal from '../../../utils/BaseModal';
import steamyears1 from '../../../../assets/img/steam-yos-badges/steamyears1_80.png';
import steamyears2 from '../../../../assets/img/steam-yos-badges/steamyears2_80.png';
import steamyears3 from '../../../../assets/img/steam-yos-badges/steamyears3_80.png';
import steamyears4 from '../../../../assets/img/steam-yos-badges/steamyears4_80.png';
import steamyears5 from '../../../../assets/img/steam-yos-badges/steamyears5_80.png';
import steamyears6 from '../../../../assets/img/steam-yos-badges/steamyears6_80.png';
import steamyears7 from '../../../../assets/img/steam-yos-badges/steamyears7_80.png';
import steamyears8 from '../../../../assets/img/steam-yos-badges/steamyears8_80.png';
import steamyears9 from '../../../../assets/img/steam-yos-badges/steamyears9_80.png';
import steamyears10 from '../../../../assets/img/steam-yos-badges/steamyears10_80.png';
import steamyears11 from '../../../../assets/img/steam-yos-badges/steamyears11_80.png';
import steamyears12 from '../../../../assets/img/steam-yos-badges/steamyears12_80.png';
import steamyears13 from '../../../../assets/img/steam-yos-badges/steamyears13_80.png';
import steamyears14 from '../../../../assets/img/steam-yos-badges/steamyears14_80.png';
import steamyears15 from '../../../../assets/img/steam-yos-badges/steamyears15_80.png';
import steamyears16 from '../../../../assets/img/steam-yos-badges/steamyears16_80.png';
import steamyears17 from '../../../../assets/img/steam-yos-badges/steamyears17_80.png';
import steamyears18 from '../../../../assets/img/steam-yos-badges/steamyears18_80.png';
import steamyears19 from '../../../../assets/img/steam-yos-badges/steamyears19_80.png';
import steamyears20 from '../../../../assets/img/steam-yos-badges/steamyears20_80.png';
import steamyears21 from '../../../../assets/img/steam-yos-badges/steamyears21_80.png';
import steamyears22 from '../../../../assets/img/steam-yos-badges/steamyears22_80.png';

const YEAR_BADGES = [steamyears1, steamyears2, steamyears3, steamyears4, steamyears5, steamyears6, steamyears7, steamyears8, steamyears9, steamyears10, steamyears11, steamyears12, steamyears13, steamyears14, steamyears15, steamyears16, steamyears17, steamyears18, steamyears19, steamyears20, steamyears21, steamyears22];

type SteamConfirmModalProps = {
    open: boolean;
    buyerAvatarUrl: string;
    buyerName: string;
    steamLevel: number;
    yearsActive: number;
    partnerSteamSignUpDate: string;
    tradeSecurityCode: string;
    onClose: () => void;
    onConfirm: () => void;
    loading?: boolean;
};

/**
 * Helper, um die korrekten CSS-Klassen für das Steam-Level-Badge zu erhalten.
 * Farbe ändert sich alle 10 Level (wiederholt sich nach 100).
 * Form: Kreis für 0-99, Hexagon für 100+.
 */
const getSteamLevelClasses = (level: number): string => {
    // Farbe basiert auf (level / 10) % 10
    const colorIndex = Math.floor(level / 10) % 10;

    // Form: Kreis bis Level 99, danach Hexagon
    const shape = level < 100 ? 'shape-circle' : 'shape-hex';

    return `steam-level-badge ${shape} color-${colorIndex}`;
};

// Helper, um die lokalen Steam "Years of Service" Abzeichen zu laden.
const getSteamYearsBadgeUrl = (years: number): string => {
    // Max 22 Jahre, Minimum 1 Jahr
    const clampedYears = Math.max(1, Math.min(22, years));
    // Array ist 0-indexed, Jahre starten bei 1
    return YEAR_BADGES[clampedYears - 1] || YEAR_BADGES[0];
};

const SteamConfirmModal: React.FC<SteamConfirmModalProps> = ({
                                                                 open,
                                                                 buyerAvatarUrl,
                                                                 buyerName,
                                                                 steamLevel,
                                                                 yearsActive,
                                                                 partnerSteamSignUpDate,
                                                                 tradeSecurityCode,
                                                                 onClose,
                                                                 onConfirm,
                                                                 loading = false
                                                             }) => {
    const TitleNode = (
        <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-blue-500/10 rounded-lg text-blue-500 flex items-center justify-center">
                <PhoneAndroid fontSize="small"/>
            </div>
            <Typography className="text-sm font-bold text-gray-900 dark:text-white tracking-wide">
                Confirm Trade on Steam Mobile App
            </Typography>
        </div>
    );

    return (
        <BaseModal
            open={open}
            titleNode={TitleNode}
            onSubmit={onConfirm}
            onAbort={onClose}
            submitText="I've Confirmed the Trade"
            submitColor="primary"
            maxWidth="sm"
            fullWidthSubmit={true}
            loading={loading}
        >
            <div className="space-y-4 my-1">
                {/* Introduction Text */}
                <Typography className="text-xs text-gray-600 dark:text-neutral-400 font-medium leading-relaxed px-0.5">
                    The trade offer has been initialized. To finalize the transaction, you must approve it within your Steam Mobile Authenticator.
                </Typography>

                {/* Warning Banner */}
                <div className="bg-red-500/5 dark:bg-red-950/15 border border-red-500/20 rounded-xl p-3 flex gap-3 items-start">
                    <InfoOutlined className="text-red-500 dark:text-red-400 mt-0.5 shrink-0" fontSize="small"/>
                    <div className="text-[11px] leading-relaxed text-red-800 dark:text-red-300">
                        <span className="font-bold block mb-1">
                            Important Security Verification:
                        </span>
                        Before approving, verify that the buyer's profile name, custom badges, and level on your mobile screen match the data displayed below exactly.
                        <p className="mt-1.5 text-red-600/90 dark:text-red-400/80 font-semibold">
                            Discrepancies indicate a potential API hijack. If the information does not match, decline the offer immediately!
                        </p>
                    </div>
                </div>

                {/* Steam Badge Box mit echtem Steam-Theming */}
                <div className="bg-[#171a21] border border-[#2a475e]/40 rounded-2xl p-5 flex flex-col items-center shadow-2xl relative overflow-hidden">
                    {/* Steam Style Background Glow Effect */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-48 bg-[#66c0f4]/5 blur-[60px] pointer-events-none" />

                    <Typography className="text-[10px] uppercase font-extrabold tracking-widest text-[#66c0f4]/80 mb-3.5 z-10">
                        Active Trade Partner
                    </Typography>

                    {/* Steam Profile Container Mimic */}
                    <div className="flex items-center gap-3.5 bg-gradient-to-r from-[#1b2838] to-[#141f2c] border border-[#2a475e]/60 rounded-xl p-3.5 w-full max-w-[360px] shadow-xl z-10">
                        <div className="relative shrink-0 flex items-center justify-center">
                            {/* Improved Steam Avatar Frame Styling */}
                            <div className="p-[2px] bg-gradient-to-b from-[#c7d5e0] via-[#3a6886] to-[#67c1f5] shadow-md">
                                <Avatar
                                    src={buyerAvatarUrl}
                                    className="w-12 h-12 rounded-none"
                                    variant="square"
                                />
                            </div>
                        </div>

                        <div className="flex flex-col min-w-0 flex-1 justify-center">
                            <Typography className="text-[15px] font-bold tracking-wide font-sans truncate text-[#e1e7ec] mb-1.5 leading-none">
                                {buyerName}
                            </Typography>

                            {/* Original Steam Badges Layout */}
                            <div className="flex items-center gap-2.5">
                                {/* Steam Level Badge mit Custom CSS */}
                                <Tooltip title="Steam Level" arrow placement="bottom">
                                    <div className={`${getSteamLevelClasses(steamLevel)} select-none scale-95 origin-left`}>
                                        {steamLevel}
                                    </div>
                                </Tooltip>

                                {/* Steam Years Active Badge */}
                                <Tooltip
                                    title={partnerSteamSignUpDate ? `Member since ${new Date(partnerSteamSignUpDate).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}` : `${yearsActive} Years of Service`} arrow placement="bottom">
                                    <div className="h-8 w-8 flex items-center justify-center">
                                        <img
                                            src={getSteamYearsBadgeUrl(yearsActive)}
                                            alt={`${yearsActive} Years of Service`}
                                            className="w-full h-full object-contain select-none"
                                        />
                                    </div>
                                </Tooltip>
                            </div>
                        </div>
                    </div>

                    {/* Safety Token Message */}
                    <div className="w-full max-w-[360px] mt-4 border-t border-[#2a475e]/30 pt-3.5 z-10">
                        <Typography className="text-[9px] font-extrabold text-[#66c0f4]/80 uppercase tracking-widest mb-1.5 text-center">
                            Verification Token
                        </Typography>
                        <Typography className="text-sm bg-[#101722] p-2 rounded-lg border border-[#2a475e]/50 font-mono text-emerald-400 text-center select-all tracking-widest font-bold shadow-inner">
                            #{tradeSecurityCode}
                        </Typography>
                    </div>
                </div>
            </div>
        </BaseModal>
    );
};

export default SteamConfirmModal;